import React, { useState, useEffect, useRef } from 'react';
import {
  Autocomplete,
  TextField,
  Paper,
  Typography,
  Box,
  Chip,
  Popper,
  InputAdornment,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Navigation as NavigationIcon,
  Folder as FolderIcon,
  Description as DescriptionIcon,
  Keyboard as KeyboardIcon,
  EditNote as EditNoteIcon,
  Assessment,
  TableBar as TableBarIcon,
  Build as BuildOutlined,
  Style as StyleIcon,
  Security,
  Message as MessageIcon,
  NotificationImportant as NotificationImportantIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import './style.css';

const MENU_BASE_URL = process.env.REACT_APP_MENU_BASE_URL || 'https://app.myplat.net.br';

const GlobalSearch = ({ menuJson = [], onNavigate }) => {
  const [searchValue, setSearchValue] = useState('');
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [popupUrl, setPopupUrl] = useState(null);
  const navigate = useNavigate();
  const searchRef = useRef(null);

  // Menu de configurações estático
  const configurationMenuItems = [
    {
      id: 'config-editor',
      title: 'Editor',
      path: 'Centro de Criação > Editor',
      url: 'listComponentes',
      level: 1,
      type: 'config',
      category: 'Centro de Criação',
      keywords: ['editor', 'componentes', 'criacao', 'desenvolvimento']
    },
    {
      id: 'config-formatador',
      title: 'Formatador/Relatórios',
      path: 'Centro de Criação > Formatador/Relatórios',
      url: 'formatReport',
      level: 1,
      type: 'config',
      category: 'Centro de Criação',
      keywords: ['formatador', 'relatorios', 'reports', 'formato', 'criacao']
    },
    {
      id: 'config-entidades',
      title: 'Entidades',
      path: 'Centro de Criação > Entidades',
      url: null, // Modal especial
      level: 1,
      type: 'config-modal',
      category: 'Centro de Criação',
      keywords: ['entidades', 'entities', 'tabelas', 'dados', 'criacao']
    },
    {
      id: 'config-scripts',
      title: 'Scripts/Relatórios',
      path: 'Centro de Criação > Scripts/Relatórios',
      url: 'typeEdition',
      level: 1,
      type: 'config',
      category: 'Centro de Criação',
      keywords: ['scripts', 'relatorios', 'automacao', 'codigo', 'criacao']
    },
    {
      id: 'config-tags',
      title: 'Tags - Parâmetros',
      path: 'Tags - Parâmetros',
      url: 'parameterConfig',
      level: 0,
      type: 'config',
      category: 'Configurações',
      keywords: ['tags', 'parametros', 'configuracao', 'params', 'settings']
    },
    {
      id: 'config-permissoes',
      title: 'Permissões',
      path: 'Permissões',
      url: 'usuarioList',
      level: 0,
      type: 'config',
      category: 'Configurações',
      keywords: ['permissoes', 'usuarios', 'acesso', 'seguranca', 'users']
    },
    {
      id: 'config-templates',
      title: 'Templates',
      path: 'Mensagens > Templates',
      url: 'template',
      level: 1,
      type: 'config',
      category: 'Mensagens',
      keywords: ['templates', 'modelos', 'mensagens', 'email', 'comunicacao']
    },
    {
      id: 'config-mensagens-auto',
      title: 'Mensagens Automatizadas',
      path: 'Mensagens > Mensagens Automatizadas',
      url: 'messagesDefinition',
      level: 1,
      type: 'config',
      category: 'Mensagens',
      keywords: ['mensagens', 'automatizadas', 'automacao', 'notificacoes', 'comunicacao']
    }
  ];

  // Função para normalizar texto (remover acentos, converter para lowercase)
  const normalizeText = (text) => {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove acentos
      .replace(/[^\w\s]/g, ' ') // Remove caracteres especiais
      .replace(/\s+/g, ' ') // Remove espaços múltiplos
      .trim();
  };

  // Função para criar uma lista plana de todos os itens do menu com hierarquia
  const createMenuFlatList = (menuItems, parentPath = '', level = 0) => {
    let flatList = [];
    
    menuItems.forEach((item, index) => {
      const currentPath = parentPath ? `${parentPath} > ${item.menu}` : item.menu;
      const children = menuJson.filter(child => child.parentmenu === item.menu);
      
      // Adiciona o item atual se tem URL (é navegável)
      if (item.url) {
        const normalizedTitle = normalizeText(item.menu);
        const normalizedPath = normalizeText(currentPath);
        
        const menuItem = {
          id: item.id || item.menu,
          title: item.menu,
          path: currentPath,
          url: item.url,
          level: level,
          type: children.length > 0 ? 'parent' : 'leaf',
          keyboardShortcut: item.key_board_short_cut || null,
          keywords: [
            normalizedTitle,
            normalizedPath,
            ...(item.description || '').split(' ').map(normalizeText).filter(Boolean),
            // Adiciona palavras-chave extras baseadas no contexto
            ...(item.menu.includes('Usuário') || item.menu.includes('User') ? ['usuario', 'user', 'permissao', 'acesso'] : []),
            ...(item.menu.includes('Relatório') || item.menu.includes('Report') ? ['relatorio', 'report', 'consulta'] : []),
            ...(item.menu.includes('Cadastro') || item.menu.includes('Cadastrar') ? ['cadastro', 'criar', 'novo'] : []),
            ...(item.menu.includes('Financeiro') || item.menu.includes('Finança') ? ['financeiro', 'dinheiro', 'pagamento'] : []),
          ].filter(Boolean),
          menuItem: item
        };
        
        flatList.push(menuItem);
      }
      
      // Adiciona os filhos recursivamente
      if (children.length > 0) {
        const childItems = createMenuFlatList(children, currentPath, level + 1);
        flatList = [...flatList, ...childItems];
      }
    });
    
    return flatList;
  };

  // Função para filtrar e pontuar resultados
  const filterAndScoreResults = (searchTerm, menuList) => {
    if (!searchTerm.trim()) return [];
    
    const normalizedTerm = normalizeText(searchTerm);
    const words = normalizedTerm.split(' ').filter(Boolean);
    
    const results = menuList
      .map(item => {
        let score = 0;
        const normalizedTitle = normalizeText(item.title);
        const normalizedPath = normalizeText(item.path);
        
        // Pontuação para correspondência exata no título
        if (normalizedTitle === normalizedTerm) {
          score += 100;
        }
        
        // Pontuação para título que começa com o termo
        if (normalizedTitle.startsWith(normalizedTerm)) {
          score += 80;
        }
        
        // Pontuação para título que contém o termo
        if (normalizedTitle.includes(normalizedTerm)) {
          score += 60;
        }
        
        // Pontuação para correspondência de palavras individuais
        words.forEach(word => {
          if (normalizedTitle.includes(word)) {
            score += 25;
          }
          
          if (normalizedPath.includes(word)) {
            score += 15;
          }
          
          item.keywords.forEach(keyword => {
            if (keyword.includes(word)) {
              score += 8;
            }
            
            // Correspondência fuzzy simples
            if (keyword.length > 3 && word.length > 3) {
              const similarity = calculateSimilarity(keyword, word);
              if (similarity > 0.7) {
                score += Math.floor(similarity * 5);
              }
            }
          });
        });
        
        // Bônus para itens de nível superior
        if (item.level === 0) {
          score += 5;
        }
        
        // Bônus para itens com nomes mais curtos (mais específicos)
        if (item.title.length < 15) {
          score += 3;
        }
        
        return { ...item, score };
      })
      .filter(item => item.score > 0)
      .sort((a, b) => {
        // Primeiro por pontuação, depois por nível, depois alfabeticamente
        if (b.score !== a.score) return b.score - a.score;
        if (a.level !== b.level) return a.level - b.level;
        return a.title.localeCompare(b.title);
      })
      .slice(0, 12); // Aumentado para 12 resultados
      
    return results;
  };

  // Função simples para calcular similaridade entre strings
  const calculateSimilarity = (str1, str2) => {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    
    if (longer.length === 0) {
      return 1.0;
    }
    
    const editDistance = levenshteinDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  };

  // Algoritmo de distância de Levenshtein simplificado
  const levenshteinDistance = (str1, str2) => {
    const matrix = [];
    
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    
    return matrix[str2.length][str1.length];
  };

  const menuFlatList = React.useMemo(() => {
    const parentMenus = menuJson.filter(item => !item.parentmenu);
    const menuItems = createMenuFlatList(parentMenus);
    
    // Combina itens do menu principal com itens de configuração
    const allItems = [...menuItems, ...configurationMenuItems];
    
    return allItems;
  }, [menuJson]);

  useEffect(() => {
    const filtered = filterAndScoreResults(searchValue, menuFlatList);
    setFilteredOptions(filtered);
    
    // Só abre o painel se tem texto E tem resultados
    setIsOpen(searchValue.trim().length > 0 && filtered.length > 0);
  }, [searchValue, menuFlatList]);

  // Atalho de teclado para focar na busca (Ctrl/Cmd + K)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        if (searchRef.current) {
          searchRef.current.focus();
        }
      }
      
      // ESC para limpar
      if (event.key === 'Escape' && (searchValue || isOpen)) {
        handleClear();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [searchValue, isOpen]);

  const handleNavigate = (option) => {
    // console.log('🚀 handleNavigate chamado com:', option);
    
    if (option.type === 'config-modal' && option.id === 'config-entidades') {
      // Caso especial para Entidades (abre modal)
      // console.log('🔗 Abrindo modal de entidades');
      setSearchValue('');
      setFilteredOptions([]);
      setIsOpen(false);
      if (onNavigate) {
        onNavigate(option, 'modal');
      }
    } else if (option.url) {
      // console.log('🔗 Navegando para URL:', option.url, 'Tipo:', option.type);
      setSearchValue('');
      setFilteredOptions([]);
      setIsOpen(false);
      
      // Navegação para itens de configuração ou menu principal
      if (option.type === 'config') {
        console.log('📍 Navegando para config:', `/${option.url}`);
        navigate(`/${option.url}`);
      } else {
        const targetUrl = `${MENU_BASE_URL}/home/${option.url}`;
        console.log('📍 Abrindo menu em popup:', targetUrl);
        setPopupUrl(targetUrl);
      }
      
      if (onNavigate) {
        onNavigate(option);
      }
    } else {
      console.error('❌ Opção sem URL:', option);
    }
  };

  const handleClear = () => {
    setSearchValue('');
    setFilteredOptions([]);
    setIsOpen(false);
    if (searchRef.current) {
      searchRef.current.focus();
    }
  };

  const handleClosePopup = () => setPopupUrl(null);

  const getOptionIcon = (option) => {
    // Ícones específicos para itens de configuração
    if (option.type === 'config' || option.type === 'config-modal') {
      switch (option.id) {
        case 'config-editor':
          return <EditNoteIcon sx={{ color: '#782cdf', fontSize: 18 }} />;
        case 'config-formatador':
          return <Assessment sx={{ color: '#782cdf', fontSize: 18 }} />;
        case 'config-entidades':
          return <TableBarIcon sx={{ color: '#782cdf', fontSize: 18 }} />;
        case 'config-scripts':
          return <BuildOutlined sx={{ color: '#782cdf', fontSize: 18 }} />;
        case 'config-tags':
          return <StyleIcon sx={{ color: '#782cdf', fontSize: 18 }} />;
        case 'config-permissoes':
          return <Security sx={{ color: '#782cdf', fontSize: 18 }} />;
        case 'config-templates':
          return <MessageIcon sx={{ color: '#782cdf', fontSize: 18 }} />;
        case 'config-mensagens-auto':
          return <NotificationImportantIcon sx={{ color: '#782cdf', fontSize: 18 }} />;
        default:
          return <SettingsIcon sx={{ color: '#782cdf', fontSize: 18 }} />;
      }
    }

    // Ícones para itens do menu principal
    switch (option.type) {
      case 'parent':
        return <FolderIcon sx={{ color: '#782cdf', fontSize: 18 }} />;
      case 'leaf':
        return <DescriptionIcon sx={{ color: '#666', fontSize: 18 }} />;
      default:
        return <NavigationIcon sx={{ color: '#999', fontSize: 18 }} />;
    }
  };

  const formatPath = (path) => {
    const parts = path.split(' > ');
    if (parts.length <= 2) return path;
    
    return (
      <Box component="span">
        {parts.slice(0, -1).join(' > ')}
        <Box component="span" sx={{ fontWeight: 600, color: '#782cdf' }}>
          {' > ' + parts[parts.length - 1]}
        </Box>
      </Box>
    );
  };

  // Custom Popper component
  const CustomPopper = (props) => {
    return (
      <Popper
        {...props}
        placement="bottom-start"
        sx={{
          zIndex: 1300,
          width: '420px !important',
          '& .MuiAutocomplete-paper': {
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            marginTop: '4px',
          }
        }}
      />
    );
  };

  return (
    <>
    <Box sx={{ minWidth: 300, maxWidth: 420 }}>
      <Autocomplete
        open={isOpen}
        onOpen={() => {
          // console.log('📂 Autocomplete onOpen disparado');
          // Só abre se tem texto válido e resultados
          if (searchValue.trim().length > 0 && filteredOptions.length > 0) {
            setIsOpen(true);
          }
        }}
        onClose={(event, reason) => {
          // console.log('📂 Autocomplete onClose disparado, reason:', reason);
          setIsOpen(false);
        }}
        options={filteredOptions}
        getOptionLabel={(option) => option.title || ''}
        filterOptions={(options) => options} // Desabilitamos o filtro padrão
        inputValue={searchValue}
        value={null} // Sempre null para não manter seleção
        onInputChange={(event, newValue, reason) => {
          // console.log('✏️ onInputChange:', newValue, 'reason:', reason);
          setSearchValue(newValue);
          
          // Se limpar completamente, fecha o painel
          if (!newValue || newValue.trim().length === 0) {
            setIsOpen(false);
          }
        }}
        onChange={(event, value, reason) => {
          console.log('🔄 Autocomplete onChange disparado:', value, 'reason:', reason);
          if (value) {
            handleNavigate(value);
          } else {
            console.log('⚠️ Valor vazio no onChange');
          }
        }}
        selectOnFocus
        clearOnBlur={false}
        handleHomeEndKeys
        blurOnSelect
        PopperComponent={CustomPopper}
        renderInput={(params) => (
          <TextField
            {...params}
            ref={searchRef}
            placeholder="Buscar no menu... (Ctrl+K)"
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                borderRadius: '20px',
                color: 'white',
                fontSize: '0.875rem',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.8)',
                },
                '& input': {
                  color: 'white',
                  '&::placeholder': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    opacity: 1,
                  },
                },
              },
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {searchValue ? (
                    <IconButton
                      size="small"
                      onClick={handleClear}
                      sx={{ 
                        color: 'rgba(255, 255, 255, 0.7)',
                        '&:hover': {
                          color: 'white',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        }
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <KeyboardIcon sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: 16 }} />
                    </Box>
                  )}
                </InputAdornment>
              ),
            }}
          />
        )}
        renderOption={(props, option, { index }) => {
          return (
            <Box 
              component="li" 
              {...props}
              key={option.id || index}
              onClick={() => {
                // console.log('🖱️ renderOption onClick disparado para:', option.title);
                handleNavigate(option);
              }}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#f8f9fa !important',
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', py: 1.5 }}>
                <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                  {getOptionIcon(option)}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: '#333',
                      fontSize: '0.875rem',
                      lineHeight: 1.2,
                    }}
                  >
                    {option.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#666',
                      fontSize: '0.75rem',
                      display: 'block',
                      lineHeight: 1.1,
                      mt: 0.5,
                    }}
                  >
                    {formatPath(option.path)}
                  </Typography>
                </Box>
                <Box sx={{ ml: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                  {option.keyboardShortcut && (
                    <Chip
                      label={option.keyboardShortcut.toUpperCase()}
                      size="small"
                      sx={{
                        fontSize: '0.6rem',
                        height: 20,
                        bgcolor: '#782cdf',
                        color: 'white',
                        fontWeight: 700,
                        fontFamily: 'monospace',
                        letterSpacing: '0.5px',
                      }}
                    />
                  )}
                  {(option.type === 'config' || option.type === 'config-modal') ? (
                    <Chip
                      label="⚙️ Config"
                      size="small"
                      sx={{
                        fontSize: '0.65rem',
                        height: 20,
                        bgcolor: '#782cdf',
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                    />
                  ) : (
                    <Chip
                      label={`Nível ${option.level + 1}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: '0.65rem',
                        height: 20,
                        color: '#782cdf',
                        borderColor: '#782cdf',
                      }}
                    />
                  )}
                  {option.score > 80 && (
                    <Chip
                      label="⭐"
                      size="small"
                      sx={{
                        fontSize: '0.6rem',
                        height: 16,
                        minWidth: 16,
                        bgcolor: '#fff3cd',
                        color: '#856404',
                      }}
                    />
                  )}
                </Box>
              </Box>
              {index < filteredOptions.length - 1 && (
                <Divider sx={{ ml: 5, mr: 1 }} />
              )}
            </Box>
          );
        }}
        renderTags={() => null} // Remove renderização de tags
        clearIcon={null} // Remove ícone de limpar padrão
        noOptionsText={
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">
              {searchValue.length > 0 
                ? 'Nenhum item encontrado' 
                : 'Digite para buscar...'
              }
            </Typography>
            {searchValue.length > 0 && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                💡 Tente usar palavras-chave como "usuário", "relatório", "cadastro"
              </Typography>
            )}
          </Box>
        }
      />
    </Box>

      <Dialog
        open={Boolean(popupUrl)}
        onClose={handleClosePopup}
        fullWidth
        maxWidth="xl"
        PaperProps={{ sx: { width: 'calc(100vw - 140px)', maxWidth: 'calc(100vw - 140px)' } }}
        onClose={handleClosePopup}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Menu Preview</span>
          <IconButton aria-label="close" onClick={handleClosePopup} size="small">
            <ClearIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {popupUrl && (
            <iframe
              src={popupUrl}
              title="Menu Preview"
              style={{ border: 'none', width: '100%', height: '85vh' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GlobalSearch;